import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { AppNotification } from '@appTypes/common.types';
import * as notificationService from '@services/notificationService';
import type { BackendNotification } from '@services/notificationService';

export interface NotificationState {
  items: AppNotification[];
  unreadCount: number;
  /** True while either {@link fetchNotifications} or {@link fetchUnreadCount} is mid-request. */
  loading: boolean;
}

const initialState: NotificationState = {
  items: [],
  unreadCount: 0,
  loading: false,
};

function backendToApp(n: BackendNotification): AppNotification {
  return {
    id: n.id,
    type: 'info',
    message: n.message,
    description: n.description ?? undefined,
    timestamp: Date.parse(n.created_at) || Date.now(),
    read: n.read,
    link: n.link ?? null,
    kind: n.kind,
  };
}

export const fetchNotifications = createAsyncThunk<
  { items: AppNotification[]; unread: number },
  { limit?: number } | undefined
>('notifications/fetch', async (arg) => {
  const payload = await notificationService.listRecent(arg?.limit ?? 20);
  return {
    items: payload.items.map(backendToApp),
    unread: payload.unread_count,
  };
});

export const fetchUnreadCount = createAsyncThunk<number>(
  'notifications/unreadCount',
  async () => notificationService.getUnreadCount(),
);

export const markReadAsync = createAsyncThunk<{ id: string; remainingUnread: number }, string>(
  'notifications/markRead',
  async (id) => {
    const remaining = await notificationService.markRead(id);
    return { id, remainingUnread: remaining };
  },
);

export const markAllAsReadAsync = createAsyncThunk<number>(
  'notifications/markAllRead',
  async () => notificationService.markAllReadRequest(),
);

const notificationSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    /**
     * Local-only fallback used by code paths that fire transient toasts
     * (e.g. session-expired modal) that aren't backed by the server.
     * Kept for backward compatibility with the existing call sites; the
     * row gets a synthesised id and is not persisted to the backend.
     */
    addNotification(state, action: PayloadAction<Omit<AppNotification, 'id' | 'timestamp' | 'read'>>) {
      const notification: AppNotification = {
        ...action.payload,
        id: `local-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
        timestamp: Date.now(),
        read: false,
      };
      state.items.unshift(notification);
      state.unreadCount += 1;
      if (state.items.length > 50) {
        state.items = state.items.slice(0, 50);
      }
    },
    /** Synchronous local mark-as-read for local-only notifications (no API call). */
    markAsRead(state, action: PayloadAction<string>) {
      const notification = state.items.find((n) => n.id === action.payload);
      if (notification && !notification.read) {
        notification.read = true;
        state.unreadCount = Math.max(0, state.unreadCount - 1);
      }
    },
    /** Synchronous mark-all (used by tests + local-only notifications). The thunk variant {@link markAllAsReadAsync} handles the backend round-trip. */
    markAllAsRead(state) {
      state.items.forEach((n) => { n.read = true; });
      state.unreadCount = 0;
    },
    removeNotification(state, action: PayloadAction<string>) {
      const idx = state.items.findIndex((n) => n.id === action.payload);
      if (idx !== -1) {
        if (!state.items[idx].read) {
          state.unreadCount = Math.max(0, state.unreadCount - 1);
        }
        state.items.splice(idx, 1);
      }
    },
    clearNotifications(state) {
      state.items = [];
      state.unreadCount = 0;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNotifications.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.items;
        state.unreadCount = action.payload.unread;
      })
      .addCase(fetchNotifications.rejected, (state) => {
        state.loading = false;
      })
      .addCase(fetchUnreadCount.fulfilled, (state, action) => {
        state.unreadCount = action.payload;
      })
      .addCase(markReadAsync.fulfilled, (state, action) => {
        const { id, remainingUnread } = action.payload;
        const row = state.items.find((n) => n.id === id);
        if (row) row.read = true;
        state.unreadCount = remainingUnread;
      })
      .addCase(markAllAsReadAsync.fulfilled, (state, action) => {
        state.items.forEach((n) => { n.read = true; });
        state.unreadCount = action.payload;
      });
  },
});

export const {
  addNotification,
  markAsRead,
  markAllAsRead,
  removeNotification,
  clearNotifications,
} = notificationSlice.actions;

export default notificationSlice.reducer;
